import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { get, find } from 'underscore';
import { Button, makeStyles, shorthands, Spinner, mergeClasses } from '@fluentui/react-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { AdsStudioPanelContainer, ClarityTags, useClarityTags, getImageAssetLimitPerAccount } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

const useButtonStyles = makeStyles({
  bottonWrapper: {
    ...shorthands.padding('16px', '24px', '24px'),
  },
  button: {
    minWidth: 'auto',
    ...shorthands.padding('6px', '12px'),
    marginRight: '6px',
  },
});

const useStyles = makeStyles({
  helpLink: {
    color: '#337ab7',
    ...shorthands.textDecoration('underline'),
    fontWeight: 'normal',
    backgroundColor: 'transparent',
    ...shorthands.borderRadius('0px', '0px', '0px', '0px'),
    ...shorthands.border('none'),
  },
  overlay: {
    zIndex: 2,
  },
  spinner: {
    marginTop: '250px',
  },
});

export const AdsStudioAddAssetPanel = (props) => {
  const {
    aid, i18n, isOpen, dismiss, logger, headerText, isReadonly, notificationApi, selectedAsset, onClickSelect, showBlocker, children, isMultiSelection,
  } = props;
  const classes = useStyles();
  const buttonClasses = useButtonStyles();
  const panelRef = useRef(null);

  useClarityTags([
    ClarityTags.LaunchedAdsStudio,
    ClarityTags.LaunchedAddImageAssetsFromAdsStudio,
  ]);

  const handleJobErrors = useCallback((jobErrors) => {
    if (jobErrors.length > 0) {
      const assetLimitError = find(jobErrors, (err) => {
        const errorCode = get(err, ['body', 'value', 0, 'Code'], '');
        return errorCode === 'ImagesLimitExceededPerAccount';
      });

      if (assetLimitError) {
        const assetLimit = getImageAssetLimitPerAccount(aid, logger);
        notificationApi.showWarning(i18n.getString(_TL_('One or more images failed to upload because your account has exceeded the {{value}}-image asset limit. Please remove assets and try again.'), { value: assetLimit }));
      } else {
        const genericError = (
          <span>
            { i18n.getString(_TL_('One or more assets failed to upload. Please try again')) }
            <button className={classNames(`HelpTopic ${classes.helpLink}`)} query="help:app54863">{i18n.getString(_TL_('Learn more about asset guidelines and accepted file types.'))}</button>
          </span>
        );
        notificationApi.showWarning(genericError);
      }
    }
  }, [aid, logger, notificationApi, i18n, classes.helpLink]);

  const onSelectAddImages = useCallback(() => {
    onClickSelect(handleJobErrors);
  }, [handleJobErrors, onClickSelect]); // todo: implement

  const onRenderFooterContent = useCallback(() => (
    <div className={buttonClasses.bottonWrapper}>
      {isMultiSelection &&
        <Button
          className={mergeClasses('add-asset-button', buttonClasses.button)}
          appearance="primary"
          onClick={onSelectAddImages}
          disabled={isReadonly || !selectedAsset || !selectedAsset.length}
        >
          {i18n.getString(_TL_('Add'))}
        </Button>
      }
      <Button className={mergeClasses('cancel-panel-button', buttonClasses.button)} onClick={dismiss}>
        {i18n.getString(_TL_('Cancel'))}
      </Button>
    </div>
  ), [buttonClasses.bottonWrapper, buttonClasses.button, onSelectAddImages, isReadonly, selectedAsset, i18n, dismiss, isMultiSelection]);

  useEffect(() => {
    if (isOpen) {
      const firstNode = document.querySelector('.panel-close-button');
      const lastNode = document.querySelector('.cancel-panel-button');
      const handleTabKey = (event) => {
        if (event.key === 'Tab' && !event.shiftKey) {
          event.preventDefault();
          event.stopPropagation();
          firstNode.focus();
        }
      };
      const handleShiftTabKey = (event) => {
        if (event.key === 'Tab' && event.shiftKey) {
          event.preventDefault();
          event.stopPropagation();
          lastNode.focus();
        }
      };

      if (lastNode && firstNode && lastNode !== firstNode) {
        lastNode.addEventListener('keydown', handleTabKey);
        firstNode.addEventListener('keydown', handleShiftTabKey);
      }

      return () => {
        if (lastNode && firstNode) {
          lastNode.removeEventListener('keydown', handleTabKey);
          firstNode.removeEventListener('keydown', handleShiftTabKey);
        }
      };
    }
    return () => {};
  }, [isOpen]);

  useEffect(() => {
    if (panelRef.current) {
      const handleESCKey = (event) => {
        if (event.key === 'Escape') {
          dismiss();
        }
      };

      const target = panelRef.current;

      if (isOpen) {
        target.addEventListener('keydown', handleESCKey);
        return () => {
          target.removeEventListener('keydown', handleESCKey);
        };
      }
    }
    return () => {};
  }, [dismiss, isOpen]);

  return (
    <AdsStudioPanelContainer
      isOpen={isOpen}
      onRenderFooterContent={onRenderFooterContent}
      headerText={headerText}
      dismiss={dismiss}
      ref={panelRef}
      className="add-image-panel"
    >
      <>
        {
          showBlocker &&
          <Overlay isDarkThemed={false} className={classes.overlay}>
            <Spinner className={classes.spinner} />
          </Overlay>
        }
        {children}
      </>
    </AdsStudioPanelContainer>
  );
};

AdsStudioAddAssetPanel.propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  isOpen: PropTypes.bool.isRequired,
  dismiss: PropTypes.func.isRequired,
  headerText: PropTypes.string.isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  isReadonly: PropTypes.bool,
  aid: PropTypes.number.isRequired,
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }).isRequired,
  selectedAsset: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({})]),
  onClickSelect: PropTypes.func.isRequired,
  showBlocker: PropTypes.bool,
  isMultiSelection: PropTypes.bool,
};

AdsStudioAddAssetPanel.defaultProps = {
  isReadonly: false,
  showBlocker: false,
  selectedAsset: undefined,
  isMultiSelection: true,
};
